import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtDate, money } from "@/lib/api";

const STATUSES = ["Pending", "Paid", "Fulfilled", "Shipped"];

const Badge = ({ children, tone = "neutral", testid }) => {
  const tones = { neutral: "bg-[#E8E3D9] text-[#1A1A1A]", dark: "bg-[#1A1A1A] text-[#FDFBF7]", bad: "bg-[#F3DDD8] text-[#8B2E24]", muted: "bg-transparent border border-[#E5E0D8] text-[#8A8780]" };
  return <span data-testid={testid} className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] ${tones[tone]}`}>{children}</span>;
};

const OrderActions = ({ order, onSaved }) => {
  const archive = async () => {
    try {
      await api.patch(`/admin/orders/${order.id}/archive`, { archived: !order.archived });
      toast.success(order.archived ? "Order restored" : "Order archived — hidden from orders and sales figures");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    if (!window.confirm(`Permanently delete order ${order.ref}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${order.id}`);
      toast.success("Order deleted");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex items-center gap-5 border-t border-[#E5E0D8] pt-4 text-xs">
      <a className="underline underline-offset-4" href={`/admin/orders/${order.id}/slip`} target="_blank" rel="noreferrer" data-testid="order-packing-slip-link">Print packing slip</a>
      <button className="underline underline-offset-4" onClick={archive} data-testid="order-archive-btn">{order.archived ? "Restore order" : "Archive order"}</button>
      <button className="text-[#8B2E24] underline underline-offset-4" onClick={remove} data-testid="order-delete-btn">Delete permanently</button>
    </div>
  );
};

const OrderDialog = ({ order, onClose, onSaved }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (order) setF({ status: order.status, carrier: order.carrier || "", trackingNumber: order.trackingNumber || "", trackingUrl: order.trackingUrl || "", notifyCustomer: false });
  }, [order]);
  if (!order) return null;
  const a = order.shippingAddress;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/orders/${order.id}`, f);
      toast.success("Order updated");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const retry = async () => {
    const { data } = await api.post(`/admin/orders/${order.id}/gelato`).catch((e) => ({ data: { gelatoError: errMsg(e) } }));
    data.gelatoOrderId ? toast.success("Sent to Gelato") : toast.error(data.gelatoError || "Gelato failed");
    onSaved();
  };
  const hasPrints = order.items.some((i) => !i.isOriginal);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto rounded-none bg-[#FDFBF7]" data-testid="order-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-light">Order {order.ref}</DialogTitle>
          <DialogDescription className="text-xs">{order.customerEmail} · {fmtDate(order.createdAt)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1 text-sm">
          {order.items.map((i, n) => <p key={n}>{i.title} — {i.category} ({i.variantLabel}) × {i.quantity} · {money(i.price * i.quantity)}</p>)}
          {order.giftMessage && <p className="whitespace-pre-line border-l border-[#8C7A5B] pl-3 text-xs" data-testid="order-gift-message">Gift message: {order.giftMessage}</p>}
          <p className="pt-2 text-[#5A626A]">Shipping {money(order.shippingFee)} · Insurance {money(order.insuranceFee)} · Discount {money(order.discount)}</p>
          <p className="font-medium">Total {money(order.totalAmount)}</p>
          <p className="pt-2 text-[#5A626A]">{a.firstName} {a.lastName}, {a.addressLine1} {a.addressLine2 || ""}, {a.city} {a.state || ""} {a.postCode}, {a.country} {a.phone ? `· ${a.phone}` : ""}</p>
          {hasPrints && <p className="pt-2 text-xs">Gelato: {order.gelatoOrderId || order.gelatoError || "not sent"}</p>}
          {order.emailStatus && <p className="text-xs text-[#5A626A]">Receipt email: {order.emailStatus}</p>}
          {order.alertStatus && <p className="text-xs text-[#5A626A]" data-testid="order-alert-status">Sale alert to you: {order.alertStatus}</p>}
          {order.conflict && <p className="text-xs font-medium text-[#9B2C2C]" data-testid="order-conflict-warning">{order.conflict}</p>}
        </div>
        <div className="grid grid-cols-2 gap-3 border-t border-[#E5E0D8] pt-4">
          <label><span className="field-label mb-1">Status</span>
            <select className="admin-input" value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="order-status-select">
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
          <label><span className="field-label mb-1">Carrier</span><input className="admin-input" value={f.carrier} onChange={(e) => set("carrier", e.target.value)} placeholder="Australia Post" data-testid="order-carrier-input" /></label>
          <label><span className="field-label mb-1">Tracking number</span><input className="admin-input" value={f.trackingNumber} onChange={(e) => set("trackingNumber", e.target.value)} data-testid="order-tracking-input" /></label>
          <label><span className="field-label mb-1">Tracking URL</span><input className="admin-input" value={f.trackingUrl} onChange={(e) => set("trackingUrl", e.target.value)} data-testid="order-tracking-url-input" /></label>
          <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={f.notifyCustomer} onChange={(e) => set("notifyCustomer", e.target.checked)} data-testid="order-notify-checkbox" /> Email tracking details to customer (when status is Shipped)</label>
        </div>
        <div className="flex justify-between gap-3 pt-2">
          {hasPrints && order.status !== "Pending" && !order.gelatoOrderId ? <button className="btn-outline px-4 py-2.5" onClick={retry} data-testid="order-gelato-retry">Send to Gelato</button> : <span />}
          <button className="btn-primary px-6 py-3" disabled={busy} onClick={save} data-testid="order-save-btn">{busy ? "Saving…" : "Save"}</button>
        </div>
        <OrderActions order={order} onSaved={onSaved} />
      </DialogContent>
    </Dialog>
  );
};

export const OrdersTab = () => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [archived, setArchived] = useState(false);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/admin/orders", { params: { ...(status ? { status } : {}), archived } }).then((r) => setItems(r.data)).catch(() => {}), [status, archived]);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="bg-[#FDFBF7] p-6" data-testid="admin-orders">
      <div className="mb-6 flex items-center justify-between">
        <p className="eyebrow" data-testid="orders-count">{items.length} {archived ? "archived " : ""}{items.length === 1 ? "order" : "orders"}</p>
        <div className="flex items-center gap-5">
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} data-testid="orders-show-archived" /> Show archived</label>
          <select className="admin-input w-auto" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="orders-status-filter">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E0D8] text-left text-[10px] uppercase tracking-[0.2em] text-[#5A626A]">
              <th className="py-3">Ref</th><th>Date</th><th>Customer</th><th>Items</th><th>Total</th><th>Status</th><th>Print</th><th />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="py-6 text-[#8A8780]">{archived ? "No archived orders" : "No orders yet"}</td></tr>}
            {items.map((o) => (
              <tr key={o.id} className="border-b border-[#F0EDE6]" data-testid={`order-row-${o.ref}`}>
                <td className="py-3 font-medium">{o.ref}</td>
                <td>{fmtDate(o.createdAt)}</td>
                <td>{o.customerName}<br /><span className="text-xs text-[#8A8780]">{o.customerEmail}</span></td>
                <td className="max-w-[220px] text-xs">{o.items.map((i) => i.title).join(", ")}</td>
                <td>{money(o.totalAmount)}</td>
                <td><Badge tone={o.status === "Pending" ? "muted" : o.status === "Shipped" ? "dark" : "neutral"} testid={`order-status-${o.ref}`}>{o.status}</Badge></td>
                <td>{o.items.some((i) => !i.isOriginal) ? <Badge tone={o.gelatoOrderId ? "neutral" : o.gelatoError ? "bad" : "muted"}>{o.gelatoOrderId ? "Gelato" : o.gelatoError ? "Failed" : "—"}</Badge> : "—"}</td>
                <td className="text-right"><button className="text-xs underline underline-offset-4" onClick={() => setOpen(o)} data-testid={`order-manage-btn-${o.ref}`}>Manage</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <OrderDialog order={open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />
    </div>
  );
};
